import React, { useState, useEffect, Fragment } from "react";
import { Button, Snackbar, Box, Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { useUserAssignableRoles, useUpdateRoles } from "../../hooks/employees";
import usePermissions from "../../redux/selectors/usePermissions";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import _ from "lodash";
import { Tooltip } from "../../components/Tooltip";
import { roleNames } from "../../constants/index";
import { useTrainingObjects } from "../../hooks/apiHooks/trainingObjects";
import InstructorSessions from "../../components/InstructorSessions/InstructorSessions";
import { statusTypes } from "../../constants";
import { fetchUser } from "../../redux/actions/userActions";
import { useDispatch } from "react-redux";

const Roles = ({ employeeID, instructorID }) => {
  const { t } = useTranslation();
  const assignableRoles = useUserAssignableRoles(employeeID);
  const [roles, setRoles] = useState();
  const saveUserRoles = useUpdateRoles(employeeID, roles);
  const [open, setOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState();
  const [adminRole, setAdminRole] = useState(null);
  const { silver, validate } = usePermissions();
  const [trainingObjects, setTrainingObjects] = useState(null);
  const {
    getTrainingObjects,
    isLoading: isLoadingTrainingObjects
  } = useTrainingObjects({
    fields: {
      trainingObject: ["id", "name", "startDate", "endDate"]
    }
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (assignableRoles && !roles) {
      setRoles(assignableRoles);
      if (
        instructorID &&
        Object.entries(assignableRoles).find(
          role =>
            role[1].name == "Instructor" && role[1].status == statusTypes.active
        )
      ) {
        getTrainingObjects({
          filters: {
            instructor: instructorID
          }
        }).then(response => {
          setTrainingObjects(response.data);
        });
      }
    }
  }, [assignableRoles]);

  const updateRoles = event => {
    const roleId = parseInt(event.target.name);
    setRoles({
      ...roles,
      [roleId]: {
        ...roles[roleId],
        status: event.target.checked ? 1 : 0
      }
    });
  };

  useEffect(() => {
    if (roles) {
      Object.entries(roles).forEach(([id, role]) => {
        if (role.name === roleNames.adminConsole) setAdminRole(role);
      });
    }
  }, [roles]);

  useEffect(() => {
    if (roles) {
      const manageTeamRole = Object.values(roles).find(
        role => role.name === roleNames.manageTeam
      );
      console.log("roles", roles);
      const customRole = Object.values(roles).find(
        role => role.name === roleNames.customRole
      );
      const instructorRole = Object.values(roles).find(
        role => role.name === roleNames.instructor
      );

      if (manageTeamRole && adminRole.status) {
        setRoles({
          ...roles,
          ...(manageTeamRole && {
            [manageTeamRole.id]: { ...manageTeamRole, status: 0 }
          }),
          ...(instructorRole && {
            [instructorRole.id]: { ...instructorRole, status: 0 }
          }),
          ...(customRole && { [customRole.id]: { ...customRole, status: 0 } })
        });
      }
    }
  }, [adminRole]);

  const handleSave = async () => {
    const response = await saveUserRoles();
    if (response.message === "Success") {
      dispatch(fetchUser());
      setSaveStatus(<Alert serverity="success">{response.message}</Alert>);
    } else {
      setSaveStatus(<Alert serverity="error">{response.message}</Alert>);
    }
    setOpen(true);
  };

  const showRolesByContext = context =>
    _.orderBy(
      Object.entries(roles),
      [role => role[1].name.toLowerCase()],
      ["asc"]
    ).map(([id, role]) => {
      if (role.context !== context) return <Fragment key={id}></Fragment>;

      const disabled = adminRole
        ? (role.name === roleNames.customRole ||
            role.name === roleNames.manageTeam ||
            role.name === roleNames.instructor) &&
          !!adminRole.status
        : true;
      const checkBox = (
        <div key={id}>
          <FormControlLabel
            componentsProps={{
              typography: {
                variant: "body2"
              }
            }}
            control={
              <Checkbox
                color="primary"
                checked={!!role.status}
                disabled={
                  role.status === statusTypes.inactive ||
                  (role.name == "Instructor" && trainingObjects?.length > 0) ||
                  disabled
                }
                onChange={updateRoles}
                name={id}
              />
            }
            label={role.name}
          />
          {role.name == "Instructor" && trainingObjects?.length > 0 && (
            <InstructorSessions sessions={trainingObjects} />
          )}
        </div>
      );

      return disabled ? (
        <Tooltip title={t("administration.userRoleDisabledTooltip")} key={id}>
          <Box display="flex" flex={1}>
            {checkBox}
          </Box>
        </Tooltip>
      ) : (
        checkBox
      );
    });

  if (!roles || isLoadingTrainingObjects) return <LoadingSpinner />;

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
        <Box flex={1}>
          <Typography variant="h6" sx={{ pb: 2.5 }}>
            {t("employeeDetail.organizationalRoles")}
          </Typography>
          {showRolesByContext("Vision")}
        </Box>
        {!silver && !validate && (
          <Box flex={1} pb={2.5}>
            <Typography variant="h6" sx={{ pb: 2.5 }}>
              {t("employeeDetail.learningManagementRoles")}
            </Typography>
            {showRolesByContext("LMS")}
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <Button color="primary" variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
      >
        <div>{saveStatus}</div>
      </Snackbar>
    </Box>
  );
};

export default Roles;
